/**
 * Cross-platform install prompt manager.
 *
 * - iOS Safari: no native API exists; we show a 2-step instructional modal.
 * - Android Chrome / Edge / Samsung Internet: capture `beforeinstallprompt`
 *   (user-gesture-deferrable) and trigger the native prompt with one tap.
 * - Already-installed users: hide everything (display-mode: standalone).
 */

let deferredPrompt = null;
const listeners = new Set();

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e;
    listeners.forEach((cb) => cb());
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    listeners.forEach((cb) => cb());
  });
}

export function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export function detectPlatform() {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent || "";
  if (/iPhone|iPad|iPod/i.test(ua)) {
    const isSafari = /Safari/i.test(ua) && !/CriOS|FxiOS|EdgiOS/i.test(ua);
    return isSafari ? "ios-safari" : "ios-other";
  }
  if (/Android/i.test(ua)) return "android";
  return "desktop";
}

export function getInstallState() {
  if (isStandalone()) {
    return { canInstall: false, kind: null, hasNativePrompt: false };
  }
  const platform = detectPlatform();
  if (deferredPrompt) {
    return { canInstall: true, kind: "android", hasNativePrompt: true };
  }
  if (platform === "ios-safari") {
    return { canInstall: true, kind: "ios", hasNativePrompt: false };
  }
  if (platform === "android") {
    // Android browser without beforeinstallprompt support — show menu instructions
    return { canInstall: true, kind: "android-manual", hasNativePrompt: false };
  }
  return { canInstall: false, kind: null, hasNativePrompt: false };
}

export async function triggerNativeInstall() {
  if (!deferredPrompt) return { success: false, reason: "no-prompt" };
  try {
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    deferredPrompt = null;
    listeners.forEach((cb) => cb());
    return { success: choice.outcome === "accepted", outcome: choice.outcome };
  } catch (e) {
    return { success: false, reason: String(e) };
  }
}

export function subscribeInstallState(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}
